import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ActivityExportFormat } from '@sparkapp/shared-types';
import { activityApi } from '../services/activityApi';
import { ApiError } from '../services/apiClient';
import { queryKeys } from '../utils/queryKeys';
import type { ActivityFilters, ActivityPayload, ActivityStatus } from '../types/activity';

/** 400s carry field errors that the panel shows inline; everything else is toasted here */
function toastUnlessValidation(err: Error, fallback: string) {
  if (err instanceof ApiError && err.status === 400) return;
  toast.error(err.message || fallback);
}

export function useActivities(filters: Partial<ActivityFilters>) {
  return useQuery({
    queryKey: queryKeys.activities.list(filters as Record<string, unknown>),
    queryFn: () => activityApi.listActivities(filters),
  });
}

export function useActivity(id: number | null) {
  return useQuery({
    queryKey: queryKeys.activities.detail(id ?? 0),
    queryFn: () => activityApi.getActivity(id!),
    enabled: id !== null,
  });
}

export function useActivityFilterOptions() {
  return useQuery({
    queryKey: queryKeys.activities.filters(),
    queryFn: () => activityApi.getFilterOptions(),
    staleTime: 5 * 60_000,
  });
}

export function useExportActivities() {
  return useMutation({
    mutationFn: (format: ActivityExportFormat) => activityApi.exportActivities(format),
    onError: (err: Error) => toast.error(err.message || 'Failed to export activities'),
  });
}

export function useCreateActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ActivityPayload) => activityApi.createActivity(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
    },
    onError: (err: Error) => toastUnlessValidation(err, 'Failed to create activity'),
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ActivityPayload }) => activityApi.updateActivity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
      toast.success('Activity saved');
    },
    onError: (err: Error) => toastUnlessValidation(err, 'Failed to save activity'),
  });
}

export function useUpdateActivityStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: Exclude<ActivityStatus, 'draft'> }) =>
      activityApi.updateStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });
      const messages: Record<Exclude<ActivityStatus, 'draft'>, string> = {
        published: 'Activity is now live',
        unpublished: 'Activity unpublished',
        archived: 'Activity archived',
      };
      toast.success(messages[variables.status]);
    },
    onError: (err: Error) => toastUnlessValidation(err, 'Failed to update status'),
  });
}
